/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import type { TFunction } from 'i18next';
import { FF_APP_CREDIT_RISK } from '@/shared/utils/feature-flag';
import { TAB_KEYS, type TabKey } from '../ClientProfilePanel/helpers';

/** The credit tab only exists behind its flag. */
export type ClientDetailTab = TabKey | 'risk';

export interface ClientTabItem {
  key: ClientDetailTab;
  label: string;
  /** Rendered as a pill beside the label; absent means no pill. */
  count?: number;
}

/** Counts the tab strip can show, when their sources have loaded. */
export type ClientTabCounts = Partial<Record<ClientDetailTab, number>>;

/**
 * Builds the ordered tab list with localized labels, appending the
 * credit tab only when its feature flag is on.
 *
 * @param t - The i18n translation function for the clients namespace.
 * @param counts - Per-tab record counts, where known.
 * @returns The list of tab items.
 */
export function buildClientTabs(t: TFunction, counts: ClientTabCounts = {}): ClientTabItem[] {
  // Reading order, not storage order: `TAB_KEYS` is the set of tabs and
  // stays as it is, but a broker works down what the client wants, what
  // they were shown, and what they offered before reaching the filing.
  const order: TabKey[] = [
    'overview', 'propertiesOfInterest', 'visits', 'proposals', 'documents', 'history',
  ];
  const keys: ClientDetailTab[] = [
    ...order.filter((key) => TAB_KEYS.includes(key)),
    ...(FF_APP_CREDIT_RISK ? (['risk'] as const) : []),
  ];
  const labels: Record<string, string> = {
    overview: t('profile.tabs.overview'),
    visits: t('profile.tabs.visits'),
    propertiesOfInterest: t('profile.tabs.propertiesOfInterest'),
    documents: t('profile.tabs.documents'),
    history: t('profile.tabs.history'),
    proposals: t('profile.tabs.proposals'),
    risk: t('profile.tabs.risk'),
  };
  return keys.map((key) => ({
    key,
    label: labels[key] ?? key,
    count: counts[key],
  }));
}
